import React from "react"
import { Box } from "@chakra-ui/react"
import CountBadge from "components/elements/CountBadge"
import Float from "components/elements/Float"
import BadgePreviewRenderer from "components/BadgeBuilder/BadgeTemplateCard/BadgePreviewRenderer"
import { ExportFormat, SheetLayout, computeContainFit } from "components/BadgeBuilder/utils/sheetPresets"
import { BadgeTemplate } from "sharedTypes/badge"
import { DioboxEvent } from "sharedTypes"
import { GuestData } from "components/BadgeBuilder/utils/badgeDataResolver"

type Props = {
  format: ExportFormat
  sheet: SheetLayout | null
  columns: number | null
  rows: number | null
  selectedBadge: BadgeTemplate | null
  previewGuests: GuestData[]
  event?: DioboxEvent
  selectedCount: number
  startPosition?: number
  diagramWidth?: number
  diagramHeight?: number
}

const DEFAULT_WIDTH = 160
const DEFAULT_HEIGHT = 207

const DiagramFrame = ({ width, height, children }: {
  width: number; height: number; children: React.ReactNode
}) => (
  <Box
    w={`${width}px`}
    h={`${height}px`}
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
  >
    {children}
  </Box>
)

const StackShadow = ({ offset, width, height }:
  { offset: number; width: string; height: string }) => (
    <Box
      position="absolute"
      top={`${-offset}px`}
      left={`${-offset}px`}
      w={width}
      h={height}
      bg="white"
      borderRadius="sm"
      borderWidth={1}
      borderColor="gray.200"
      zIndex={-offset}
    />
)

const SheetPreviewDiagram = ({
  format, sheet, columns, rows, selectedBadge, previewGuests, event, selectedCount,
  startPosition = 1,
  diagramWidth = DEFAULT_WIDTH,
  diagramHeight = DEFAULT_HEIGHT,
}: Props) => {
  // Single badge: badge preview centered, with stacking if count > 1
  if (format === ExportFormat.SINGLE) {
    const showStacking = selectedCount > 1
    const stackCount = Math.min(selectedCount - 1, 3)

    // Use actual badge aspect ratio, fit within available area
    const maxW = diagramWidth - 30
    const maxH = diagramHeight - 27
    let badgeW = maxW
    let badgeH = maxH
    if (selectedBadge) {
      const scale = Math.min(maxW / selectedBadge.widthMm, maxH / selectedBadge.heightMm)
      badgeW = Math.round(selectedBadge.widthMm * scale)
      badgeH = Math.round(selectedBadge.heightMm * scale)
    }

    return (
      <DiagramFrame width={diagramWidth} height={diagramHeight}>
        <Box position="relative">
          {showStacking && Array.from({ length: stackCount }).map((_, i) => (
            <StackShadow
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              offset={(stackCount - i) * 3}
              width={`${badgeW}px`}
              height={`${badgeH}px`}
            />
          ))}
          <Box
            w={`${badgeW}px`}
            h={`${badgeH}px`}
            bg="white"
            borderRadius="sm"
            borderWidth={1}
            borderColor="gray.200"
            overflow="hidden"
            position="relative"
            zIndex={1}
          >
            {selectedBadge ? (
              <BadgePreviewRenderer
                template={selectedBadge}
                containerWidth={badgeW}
                containerHeight={badgeH}
                guestData={previewGuests[0]}
                eventData={event}
              />
            ) : (
              <Box w="full" h="full" bg="gray.100" />
            )}
          </Box>
          {showStacking && (
            <Float placement="top-end" offset="4px" zIndex={10}>
              <CountBadge count={selectedCount} boxShadow="sm" />
            </Float>
          )}
        </Box>
      </DiagramFrame>
    )
  }

  // Custom sheet not yet configured: dashed placeholder
  if (format === ExportFormat.CUSTOM_SHEET && (!columns || !rows)) {
    return (
      <DiagramFrame width={diagramWidth} height={diagramHeight}>
        <Box
          w={`${Math.round(diagramWidth * 0.75)}px`}
          h={`${Math.round(diagramHeight * 0.75)}px`}
          bg="gray.50"
          borderRadius="sm"
          borderWidth={1}
          borderColor="gray.300"
          borderStyle="dashed"
        />
      </DiagramFrame>
    )
  }

  // Sheet formats (Avery / Custom configured)
  const cols = columns || 1
  const rws = rows || 1
  const totalLabels = cols * rws

  if (!sheet) {
    // Fallback: simple grid without sheet layout details
    return (
      <DiagramFrame width={diagramWidth} height={diagramHeight}>
        <Box
          w={`${Math.round(diagramWidth * 0.75)}px`}
          h={`${Math.round(diagramHeight * 0.75)}px`}
          bg="white"
          borderRadius="sm"
          borderWidth={1}
          borderColor="gray.300"
          display="grid"
          gridTemplateColumns={`repeat(${cols}, 1fr)`}
          gridTemplateRows={`repeat(${rws}, 1fr)`}
          gap="2px"
          p="4px"
        >
          {Array.from({ length: totalLabels }).map((_, i) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              borderWidth="1px"
              borderStyle="dotted"
              borderColor="gray.200"
            />
          ))}
        </Box>
      </DiagramFrame>
    )
  }

  const pageW = sheet.pageWidthMm
  const pageH = sheet.pageHeightMm
  const pageAspect = pageW / pageH

  // Fit page into diagram bounds
  let renderW: number
  let renderH: number
  if (pageAspect > diagramWidth / diagramHeight) {
    renderW = diagramWidth
    renderH = diagramWidth / pageAspect
  } else {
    renderH = diagramHeight
    renderW = diagramHeight * pageAspect
  }

  // Scale factor: diagram pixels per mm
  const scale = renderW / pageW

  // Calculate label slot pixel sizes
  const labelSlotW = Math.floor(sheet.labelWidthMm * scale)
  const labelSlotH = Math.floor(sheet.labelHeightMm * scale)

  // Contain-fit: scale badge to fit label slot, trying both orientations
  const fitResult = selectedBadge
    ? computeContainFit(
      selectedBadge.widthMm,
      selectedBadge.heightMm,
      sheet.labelWidthMm,
      sheet.labelHeightMm,
    )
    : { scale: 1, rotated: false }

  // Badge dimensions in the label's coordinate space (post-rotation, in px).
  // Clamp to label slot so rounding (Math.round) never exceeds Math.floor'd slot.
  let badgeRenderW = labelSlotW
  let badgeRenderH = labelSlotH
  // Badge natural dimensions for the renderer (pre-rotation, in px)
  let badgeNaturalW = labelSlotW
  let badgeNaturalH = labelSlotH
  if (selectedBadge) {
    if (fitResult.rotated) {
      // After rotation: badge height becomes width, badge width becomes height
      badgeRenderW = Math.min(
        Math.round(selectedBadge.heightMm * fitResult.scale * scale), labelSlotW,
      )
      badgeRenderH = Math.min(
        Math.round(selectedBadge.widthMm * fitResult.scale * scale), labelSlotH,
      )
      // Renderer sees the badge at its natural (pre-rotation) proportions
      badgeNaturalW = badgeRenderH // pre-rotation width = post-rotation height
      badgeNaturalH = badgeRenderW // pre-rotation height = post-rotation width
    } else {
      badgeRenderW = Math.min(
        Math.round(selectedBadge.widthMm * fitResult.scale * scale), labelSlotW,
      )
      badgeRenderH = Math.min(
        Math.round(selectedBadge.heightMm * fitResult.scale * scale), labelSlotH,
      )
      badgeNaturalW = badgeRenderW
      badgeNaturalH = badgeRenderH
    }
  }

  // Calculate padding and gap in px
  const padX = sheet.marginLeftRightMm * scale
  const padY = sheet.marginTopBottomMm * scale
  const gapX = sheet.columnGapMm * scale
  const gapY = sheet.rowGapMm * scale

  const startOffset = (startPosition ?? 1) - 1
  const firstSheetCapacity = totalLabels - startOffset
  const filledOnFirstSheet = Math.min(selectedCount, firstSheetCapacity)
  const remaining = selectedCount - filledOnFirstSheet
  const sheetCount = remaining > 0 ? 1 + Math.ceil(remaining / totalLabels) : 1
  const showSheetStacking = sheetCount > 1
  const stackSheetCount = Math.min(sheetCount - 1, 2)

  return (
    <DiagramFrame width={diagramWidth} height={diagramHeight}>
      <Box position="relative">
        {showSheetStacking && Array.from({ length: stackSheetCount }).map((_, i) => (
          <StackShadow
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            offset={(stackSheetCount - i) * 3}
            width={`${Math.round(renderW)}px`}
            height={`${Math.round(renderH)}px`}
          />
        ))}
        <Box
          w={`${Math.round(renderW)}px`}
          h={`${Math.round(renderH)}px`}
          bg="white"
          borderRadius="sm"
          borderWidth={1}
          borderColor="gray.300"
          overflow="hidden"
          position="relative"
          zIndex={1}
          display="grid"
          gridTemplateColumns={`repeat(${cols}, ${labelSlotW}px)`}
          gridTemplateRows={`repeat(${rws}, ${labelSlotH}px)`}
          padding={`${padY}px ${padX}px`}
          columnGap={`${gapX}px`}
          rowGap={`${gapY}px`}
        >
          {Array.from({ length: totalLabels }).map((_, i) => {
            const isFilled = i >= startOffset && i < startOffset + filledOnFirstSheet
            const guestIndex = i - startOffset
            const guestForSlot = previewGuests.length > 0
              ? previewGuests[guestIndex % previewGuests.length]
              : undefined

            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                minW={0}
                minH={0}
                overflow="hidden"
                borderWidth="1px"
                borderStyle="dotted"
                borderColor="gray.200"
              >
                {isFilled && selectedBadge && (
                  <Box
                    w={`${badgeRenderW}px`}
                    h={`${badgeRenderH}px`}
                    overflow="hidden"
                  >
                    <Box
                      w={`${badgeNaturalW}px`}
                      h={`${badgeNaturalH}px`}
                      transform={fitResult.rotated ? "rotate(-90deg) translateX(-100%)" : undefined}
                      transformOrigin={fitResult.rotated ? "top left" : undefined}
                    >
                      <BadgePreviewRenderer
                        template={selectedBadge}
                        containerWidth={badgeNaturalW}
                        containerHeight={badgeNaturalH}
                        guestData={guestForSlot}
                        eventData={event}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
        {showSheetStacking && (
          <Float placement="top-end" offset="4px" zIndex={10}>
            <CountBadge count={sheetCount} boxShadow="sm" />
          </Float>
        )}
      </Box>
    </DiagramFrame>
  )
}

export default SheetPreviewDiagram
